import { ReactNode, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  BarChart,
  Description,
  DeveloperBoard,
  Settings,
  TableChart,
} from '@mui/icons-material'
import { useStytchIsAuthorized } from '@stytch/react/b2b'
import { useProjectContext } from '../project-tables/ProjectProvider'
import HorizontalTab from '../tabs/HorizontalTab'
import HorizontalTabs from '../tabs/HorizontalTabs'
import { useSelectedWorkflowContext } from '../workflows/SelectedWorkflowProvider'

export default function WorkflowTabs() {
  const { project } = useProjectContext()
  const { selectedWorkflow } = useSelectedWorkflowContext()
  const navigate = useNavigate()
  const { pathname, search } = useLocation()

  const { isAuthorized: canEditProject } = useStytchIsAuthorized(
    'pd.project',
    'edit',
  )

  const isModelTrainingWorkflow = !!selectedWorkflow?.project_model_id

  // When switching between workflow types, redirect between metrics/model paths
  useEffect(() => {
    const isOnMetrics = pathname.includes(`${project.id}/metrics`)
    const isOnModel = pathname.includes(`${project.id}/model`)

    if (isModelTrainingWorkflow && isOnMetrics) {
      navigate(`model${search}`, { replace: true })
    } else if (!isModelTrainingWorkflow && isOnModel) {
      navigate(`metrics${search}`, { replace: true })
    }
  }, [isModelTrainingWorkflow, pathname, search, project.id, navigate])

  const routes = [
    {
      label: isModelTrainingWorkflow ? 'Model' : 'Metrics',
      to: isModelTrainingWorkflow ? 'model' : 'metrics',
      icon: isModelTrainingWorkflow ? (
        <DeveloperBoard fontSize="small" />
      ) : (
        <BarChart fontSize="small" />
      ),
      keepQueryParams: true,
    },
    {
      label: 'Documents',
      to: 'documents',
      icon: <Description fontSize="small" />,
      keepQueryParams: true,
    },
    {
      label: 'Data',
      to: 'data',
      icon: <TableChart fontSize="small" />,
      keepQueryParams: true,
    },
    canEditProject
      ? {
          label: 'Settings',
          to: 'settings/general', // Using general so that we don't hit index route which causes issues with keeping the query params
          icon: <Settings fontSize="small" />,
          keepQueryParams: true,
        }
      : null,
  ].filter(Boolean) as {
    label: string
    to: string
    icon?: ReactNode
    keepQueryParams?: boolean
  }[]

  const selectedRouteIndex = routes.findIndex((route) => {
    const routeSection = route.to.split('/')[0]
    // During workflow switches, metrics/model paths are interchangeable for highlighting
    if (routeSection === 'model' || routeSection === 'metrics') {
      return (
        pathname.includes(`${project.id}/metrics`) ||
        pathname.includes(`${project.id}/model`)
      )
    }
    return pathname.includes(`${project.id}/${routeSection}`)
  })

  return (
    <HorizontalTabs>
      {routes.map((route, index) => {
        return (
          <HorizontalTab
            key={route.to}
            selected={selectedRouteIndex === index}
            {...route}
          />
        )
      })}
    </HorizontalTabs>
  )
}
